import type { Flashcard, FlashcardCollection, FlashcardNote } from "@japa-reader/contracts";
import { CalendarDays, PauseCircle, RotateCcw, Search, Tag, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { deckPath } from "../lib/flashcard-search";
import { fieldPlainText } from "../lib/flashcard-templates";

type Props = {
  collection: FlashcardCollection;
  cards: Flashcard[];
  query: string;
  setQuery: (value: string) => void;
  selected: Set<string>;
  setSelected: (value: Set<string>) => void;
  onEditNote: (note: FlashcardNote) => void;
  onSuspend: () => void;
  onFlag: (flag: Flashcard["flag"]) => void;
  onMove: (deckId: string) => void;
  onDelete: () => void;
  onReset: () => void;
  onSetDue: () => void;
  onAddTags: () => void;
  onRemoveTags: () => void;
  onFindReplace: () => void;
  onChangeNoteType: (noteTypeId: string) => void;
  onReposition: () => void;
  onFindDuplicates: () => void;
};

type SortKey = "front" | "back" | "deck" | "state" | "due" | "tags";
type SavedSearch = { id: string; name: string; query: string };

const SAVED_SEARCHES_KEY = "japa-reader-flashcard-saved-searches";

export function FlashcardBrowser(props: Props) {
  const { collection, cards, query, setQuery, selected, setSelected, onEditNote, onSuspend, onFlag, onMove, onDelete, onReset, onSetDue, onAddTags, onRemoveTags, onFindReplace, onChangeNoteType, onReposition, onFindDuplicates } = props;
  const [sortKey, setSortKey] = useState<SortKey>("due");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(readSavedSearches);
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window !== "undefined" && window.innerWidth > 900);
  const notes = new Map(collection.notes.map((note) => [note.id, note]));
  const noteTypes = new Map(collection.noteTypes.map((type) => [type.id, type]));
  const decks = new Map(collection.decks.map((deck) => [deck.id, deck]));
  const sortedCards = [...cards].sort((left, right) => compareValues(cardSortValue(left, sortKey), cardSortValue(right, sortKey)) * sortDirection);
  const allSelected = sortedCards.length > 0 && sortedCards.every((card) => selected.has(card.id));
  const tagCounts = new Map<string, number>();
  for (const card of collection.cards) for (const tag of notes.get(card.noteId)?.tags || []) tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);

  useEffect(() => {
    try {
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches));
    } catch {
      // Saved searches remain available for the current session when storage is unavailable.
    }
  }, [savedSearches]);

  function cardSortValue(card: Flashcard, key: SortKey): string | number {
    const note = notes.get(card.noteId);
    const type = noteTypes.get(note?.noteTypeId || "");
    if (key === "front") return note ? fieldPlainText(note, type?.fields[0]?.name || "") : "";
    if (key === "back") return note ? fieldPlainText(note, type?.fields[1]?.name || "") : "";
    if (key === "deck") return decks.get(card.deckId)?.name || "";
    if (key === "state") return stateLabel(card);
    if (key === "tags") return note?.tags.join(" ") || "";
    return card.due;
  }

  function changeSort(key: SortKey) {
    if (sortKey === key) setSortDirection((direction) => direction === 1 ? -1 : 1);
    else {
      setSortKey(key);
      setSortDirection(1);
    }
  }

  function saveCurrentSearch() {
    if (!query.trim()) return;
    const name = window.prompt("Nome desta busca salva:", query.trim());
    if (!name?.trim()) return;
    setSavedSearches((current) => [...current, { id: crypto.randomUUID(), name: name.trim(), query: query.trim() }]);
  }

  return <section className="browser-view">
    <div className="browser-heading"><div><span className="eyebrow">EXPLORAR</span><h1>Cartões e notas</h1></div><div className="browser-heading-actions"><span>{cards.length} resultado(s)</span><button onClick={onFindDuplicates}>Localizar duplicatas</button></div></div>
    <div className="browser-search"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar texto, tag:verbo, deck:Japonês, is:due, prop:ivl>30…" />{query.trim() && <button onClick={saveCurrentSearch}>Salvar busca</button>}</div>
    {selected.size > 0 && <div className="bulk-toolbar">
      <strong>{selected.size} selecionado(s)</strong>
      <button onClick={onSuspend}><PauseCircle size={15} /> Suspender</button>
      <button onClick={onReset}><RotateCcw size={15} /> Redefinir</button>
      <button onClick={onSetDue}><CalendarDays size={15} /> Definir data</button>
      <button onClick={onReposition}>Reposicionar</button>
      <button onClick={onAddTags}><Tag size={15} /> Adicionar tags</button>
      <button onClick={onRemoveTags}>Remover tags</button>
      <button onClick={onFindReplace}>Localizar/substituir</button>
      <select defaultValue="" onChange={(event) => { onMove(event.target.value); event.target.value = ""; }}><option value="" disabled>Mover para…</option>{collection.decks.filter((deck) => deck.kind === "standard").map((deck) => <option key={deck.id} value={deck.id}>{deckPath(deck, collection.decks)}</option>)}</select>
      <select defaultValue="" onChange={(event) => { onChangeNoteType(event.target.value); event.target.value = ""; }}><option value="" disabled>Alterar tipo de nota…</option>{collection.noteTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select>
      <span className="flag-palette">{([1, 2, 3, 4, 5, 6, 7, 0] as Flashcard["flag"][]).map((flag) => <button key={flag} className={`flag-dot flag-${flag}`} onClick={() => onFlag(flag)} aria-label={`Bandeira ${flag}`} />)}</span>
      <button className="danger-action" onClick={onDelete}><Trash2 size={15} /> Excluir</button>
    </div>}

    <div className="browser-workspace">
      <details className="browser-sidebar" open={filtersOpen} onToggle={(event) => setFiltersOpen(event.currentTarget.open)}>
        <summary>Filtros</summary>
        <BrowserFilterGroup title="Estados"><button onClick={() => setQuery("")}>Todos <span>{collection.cards.length}</span></button><button onClick={() => setQuery("is:due")}>Hoje <span>{collection.cards.filter((card) => !card.suspended && card.due <= Date.now()).length}</span></button><button onClick={() => setQuery("is:new")}>Novos</button><button onClick={() => setQuery("is:learning")}>Aprendendo</button><button onClick={() => setQuery("is:suspended")}>Suspensos</button><button onClick={() => setQuery("is:buried")}>Ocultos</button></BrowserFilterGroup>
        <BrowserFilterGroup title="Baralhos">{collection.decks.map((deck) => <button key={deck.id} onClick={() => setQuery(`deck:${quoteSearch(deckPath(deck, collection.decks))}`)}>{deckPath(deck, collection.decks)} <span>{collection.cards.filter((card) => card.deckId === deck.id).length}</span></button>)}</BrowserFilterGroup>
        <BrowserFilterGroup title="Tipos de nota">{collection.noteTypes.map((type) => <button key={type.id} onClick={() => setQuery(`note:${quoteSearch(type.name)}`)}>{type.name} <span>{collection.notes.filter((note) => note.noteTypeId === type.id).length}</span></button>)}</BrowserFilterGroup>
        <BrowserFilterGroup title="Tags">{[...tagCounts].sort(([left], [right]) => left.localeCompare(right)).map(([tag, count]) => <button key={tag} style={{ paddingLeft: `${10 + Math.max(0, tag.split("::").length - 1) * 12}px` }} onClick={() => setQuery(`tag:${quoteSearch(tag)}`)}>{tag.split("::").at(-1)} <span>{count}</span></button>)}</BrowserFilterGroup>
        {savedSearches.length > 0 && <BrowserFilterGroup title="Buscas salvas">{savedSearches.map((saved) => <div className="saved-search" key={saved.id}><button onClick={() => setQuery(saved.query)}>{saved.name}</button><button onClick={() => setSavedSearches((current) => current.filter((candidate) => candidate.id !== saved.id))} aria-label={`Excluir busca ${saved.name}`}>×</button></div>)}</BrowserFilterGroup>}
      </details>

      <div className="browser-table-wrap"><table className="browser-table"><thead><tr><th><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(sortedCards.map((card) => card.id)))} aria-label="Selecionar todos" /></th>{([['front', 'Frente'], ['back', 'Verso'], ['deck', 'Baralho'], ['state', 'Estado'], ['due', 'Próxima revisão'], ['tags', 'Tags']] as Array<[SortKey, string]>).map(([key, label]) => <th key={key}><button onClick={() => changeSort(key)}>{label}{sortKey === key ? sortDirection === 1 ? " ↑" : " ↓" : ""}</button></th>)}</tr></thead><tbody>{sortedCards.map((card) => {
        const note = notes.get(card.noteId);
        const type = noteTypes.get(note?.noteTypeId || "");
        const deck = decks.get(card.deckId);
        const fields = type?.fields || [];
        return <tr key={card.id} className={`${card.suspended ? "is-suspended" : ""} ${card.flag ? `has-flag flag-${card.flag}` : ""}`} onDoubleClick={() => note && onEditNote(note)}><td><input type="checkbox" checked={selected.has(card.id)} onChange={() => { const next = new Set(selected); if (next.has(card.id)) next.delete(card.id); else next.add(card.id); setSelected(next); }} aria-label={`Selecionar ${card.id}`} /></td><td>{truncate(note ? fieldPlainText(note, fields[0]?.name || "") : "", 90)}</td><td>{truncate(note ? fieldPlainText(note, fields[1]?.name || "") : "", 90)}</td><td>{deck ? deckPath(deck, collection.decks) : "—"}</td><td><span className={`state-badge state-${card.state}`}>{stateLabel(card)}</span></td><td>{card.state === "new" ? "Novo" : dueLabel(card.due)}</td><td>{note?.tags.map((tag) => <span className="tag-chip" key={tag}>{tag}</span>)}</td></tr>;
      })}</tbody></table>{!cards.length && <div className="browser-empty"><Search size={24} /><p>Nenhum cartão corresponde à busca.</p></div>}</div>
    </div>
  </section>;
}

function BrowserFilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return <section><strong>{title}</strong>{children}</section>;
}

function readSavedSearches(): SavedSearch[] {
  try {
    const value = JSON.parse(localStorage.getItem(SAVED_SEARCHES_KEY) || "[]") as SavedSearch[];
    return Array.isArray(value) ? value.filter((item) => item && typeof item.name === "string" && typeof item.query === "string").slice(0, 100) : [];
  } catch {
    return [];
  }
}

function compareValues(left: string | number, right: string | number) {
  return typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

function quoteSearch(value: string) {
  return /\s/.test(value) ? `"${value.replace(/"/g, '\\"')}"` : value;
}

function stateLabel(card: Flashcard) {
  if (card.suspended) return "Suspenso";
  if (card.buriedUntil && card.buriedUntil > Date.now()) return "Oculto";
  return ({ new: "Novo", learning: "Aprendendo", review: "Revisão", relearning: "Reaprendendo" })[card.state];
}

function dueLabel(timestamp: number) {
  const days = Math.ceil((timestamp - Date.now()) / 86_400_000);
  if (days <= 0) return "Hoje";
  if (days === 1) return "Amanhã";
  return new Date(timestamp).toLocaleDateString("pt-BR");
}

function truncate(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}
